import { BusyContent, LoadingState } from './ui/Primitives';
import React, { useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { getBackendHost } from '../utils/backendHost'
import flashMessage from '../components/HelperFunctions'
import './UserActionTable.css'

const DEFAULT_LIMIT = 50
const DEFAULT_PAGE_SIZE = 25
const MAX_LIMIT = 1000

const QUICK_FILTERS = [
  { value: 'all', label: 'Application events' },
  { value: 'scanner_probe', label: 'Scanner traffic' },
  { value: 'failures', label: 'Failures' },
  { value: 'tool_call', label: 'Tool calls' },
  { value: 'agent_run', label: 'Agent runs' },
  { value: 'navigation', label: 'Navigation' }
]

const sanitizeLimit = (value) => {
  const parsed = parseInt(value, 10)
  if (Number.isNaN(parsed)) return DEFAULT_LIMIT
  return Math.min(MAX_LIMIT, Math.max(1, parsed))
}

const titleCase = (value) => String(value || 'unknown')
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/[-_]/g, ' ')
  .replace(/\b\w/g, letter => letter.toUpperCase())

const formatTimestampPst = (value) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('en-US', {
    timeZone: 'America/Los_Angeles',
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  })
}

const formatRelativeTime = (value) => {
  const timestamp = new Date(value).getTime()
  if (Number.isNaN(timestamp)) return 'Unknown time'
  const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1000))
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

const formatDuration = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—'
  const milliseconds = Number(value)
  if (milliseconds >= 60000) return `${(milliseconds / 60000).toFixed(1)} min`
  if (milliseconds >= 1000) return `${(milliseconds / 1000).toFixed(2)} s`
  return `${Math.round(milliseconds)} ms`
}

const contextEntries = (context) => Object.entries(context || {})
  .filter(([, value]) => ['string', 'number', 'boolean'].includes(typeof value))
  .slice(0, 8)

const statusClass = (status) => ['succeeded', 'healthy', 'ready'].includes(status)
  ? 'success'
  : ['failed', 'unavailable'].includes(status) ? 'failure' : 'attention'

const stringifyJson = (value) => JSON.stringify(value, null, 2)

const storedEventData = rawRecord => {
  const value = rawRecord?.data
  if (value && typeof value === 'object') return value
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch (_error) {
      return {}
    }
  }
  return {}
}

const isClickEvent = (row) => String(row?.name || row?.actionType || '').toLowerCase() === 'click'

const clickedTarget = (row) => {
  const context = row?.context || {}
  return context.value || context.target || context.element || context.path || null
}

const eventDisplayName = (row) => {
  if (row?.toolName) return row.toolName
  const target = isClickEvent(row) ? clickedTarget(row) : null
  return target ? `Click · ${target}` : row?.name || 'Unknown event'
}

const contextLabel = (row, key) => (
  isClickEvent(row) && ['value', 'target', 'element', 'path'].includes(key)
    ? 'Clicked'
    : titleCase(key)
)

const isErrorEvent = (row) => {
  const values = [row?.name, row?.actionType, row?.status]
    .map(value => String(value || '').toLowerCase())
  return values.some(value => value.includes('error') || value.includes('fail') || value === 'unavailable')
}

const STACK_KEYS = new Set(['stack', 'stacktrace', 'traceback', 'exceptionstack'])

const findStackTrace = (value, depth = 0) => {
  if (depth > 10 || value === null || value === undefined) return null
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if ((trimmed.startsWith('{') || trimmed.startsWith('[')) && trimmed.length < 1000000) {
      try {
        return findStackTrace(JSON.parse(trimmed), depth + 1)
      } catch (error) {
        // The stored value is ordinary text rather than nested JSON.
      }
    }
    return trimmed.includes('Traceback (most recent call last)') || /\n\s*at\s+\S/.test(trimmed)
      ? value
      : null
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const stack = findStackTrace(item, depth + 1)
      if (stack) return stack
    }
    return null
  }
  if (typeof value !== 'object') return null

  for (const [key, item] of Object.entries(value)) {
    if (STACK_KEYS.has(key.replace(/[_-]/g, '').toLowerCase()) && typeof item === 'string' && item) return item
  }
  for (const item of Object.values(value)) {
    const stack = findStackTrace(item, depth + 1)
    if (stack) return stack
  }
  return null
}

const UserActionTable = () => {
  const backendHost = getBackendHost()
  const [rows, setRows] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [limit, setLimit] = useState(String(DEFAULT_LIMIT))
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [currentPage, setCurrentPage] = useState(1)
  const [globalSearch, setGlobalSearch] = useState('')
  const [selectedEmail, setSelectedEmail] = useState('all')
  const [selectedAction, setSelectedAction] = useState('all')
  const [quickFilter, setQuickFilter] = useState('all')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [lastLoadedAt, setLastLoadedAt] = useState(null)
  const [selectedRow, setSelectedRow] = useState(null)
  const [rawRecord, setRawRecord] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState('')
  const [copied, setCopied] = useState(false)
  const detailRequest = useRef(0)
  const listRequest = useRef(0)
  const eventCategory = quickFilter === 'scanner_probe' ? 'scanner' : 'application'

  const authConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('jwt_authorization')}` }
  })

  const handleLoad = async () => {
    const requestId = ++listRequest.current
    setIsLoading(true)
    const normalizedLimit = sanitizeLimit(limit)
    setLimit(String(normalizedLimit))
    try {
      const response = await axios.get(
        `${backendHost}/v2/observability/user-actions`,
        { ...authConfig(), params: { limit: normalizedLimit, category: eventCategory } }
      )
      if (requestId !== listRequest.current) return
      const payload = Array.isArray(response.data) ? response.data : []
      setRows(payload.map(row => ({
        ...row,
        _timestampSort: new Date(row.timestamp).getTime() || 0
      })))
      setCurrentPage(1)
      setLastLoadedAt(new Date())
    } catch (error) {
      if (requestId === listRequest.current) flashMessage('error', 'Latest events could not be loaded')
    } finally {
      if (requestId === listRequest.current) setIsLoading(false)
    }
  }

  useEffect(() => {
    setRows([])
    setSelectedAction('all')
    setSelectedEmail('all')
    handleLoad()
    return () => { listRequest.current += 1 }
  }, [eventCategory])

  const closeDetails = () => {
    detailRequest.current += 1
    setSelectedRow(null)
    setRawRecord(null)
    setDetailError('')
    setCopied(false)
  }

  useEffect(() => {
    if (!selectedRow) return undefined
    const previousOverflow = document.body.style.overflow
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeDetails()
    }
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [selectedRow])

  const openDetails = async (row) => {
    const requestId = detailRequest.current + 1
    detailRequest.current = requestId
    setSelectedRow(row)
    setRawRecord(null)
    setDetailError('')
    setDetailLoading(true)
    setCopied(false)
    try {
      const response = await axios.get(
        `${backendHost}/v2/observability/user-actions/${row.id}`,
        authConfig()
      )
      if (detailRequest.current === requestId) setRawRecord(response.data)
    } catch (error) {
      if (detailRequest.current === requestId) setDetailError('The complete event record could not be loaded.')
    } finally {
      if (detailRequest.current === requestId) setDetailLoading(false)
    }
  }

  const emailOptions = useMemo(() => [...new Set(rows.map(row => row.email).filter(Boolean))].sort(), [rows])
  const actionOptions = useMemo(() => [...new Set(rows.map(row => row.name).filter(Boolean))].sort(), [rows])

  const filteredRows = useMemo(() => {
    const query = globalSearch.trim().toLowerCase()
    const startMs = startDate ? new Date(`${startDate}T00:00:00`).getTime() : null
    const endMs = endDate ? new Date(`${endDate}T23:59:59`).getTime() : null
    return rows.filter(row => {
      if (selectedEmail !== 'all' && row.email !== selectedEmail) return false
      if (selectedAction !== 'all' && row.name !== selectedAction) return false
      if (quickFilter === 'failures' && !['failed', 'unavailable', 'rejected'].includes(row.status)) return false
      if (quickFilter === 'navigation' && !['PageView', 'SectionView'].includes(row.name)) return false
      if (!['all', 'failures', 'navigation'].includes(quickFilter) && row.eventType !== quickFilter) return false
      if (startMs && row._timestampSort < startMs) return false
      if (endMs && row._timestampSort > endMs) return false
      if (!query) return true
      const searchable = [
        row.name,
        row.actionType,
        row.eventType,
        row.service,
        row.status,
        row.email,
        row.userId,
        row.traceId,
        row.runId,
        row.sessionId,
        row.toolName,
        ...contextEntries(row.context).flatMap(([key, value]) => [key, value])
      ]
      return searchable.some(value => String(value || '').toLowerCase().includes(query))
    }).sort((a, b) => b._timestampSort - a._timestampSort)
  }, [rows, selectedEmail, selectedAction, quickFilter, startDate, endDate, globalSearch])

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize))
  const pagedRows = filteredRows.slice((currentPage - 1) * pageSize, currentPage * pageSize)
  const failures = rows.filter(row => row.eventType !== 'scanner_probe' && ['failed', 'unavailable', 'rejected'].includes(row.status)).length
  const toolCalls = rows.filter(row => row.eventType === 'tool_call').length
  const uniqueUsers = new Set(rows.map(row => row.email || row.userId).filter(Boolean)).size

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages)
  }, [currentPage, totalPages])

  const clearFilters = () => {
    setGlobalSearch('')
    setSelectedEmail('all')
    setSelectedAction('all')
    setQuickFilter('all')
    setStartDate('')
    setEndDate('')
    setCurrentPage(1)
  }

  const copyRawRecord = async () => {
    if (!rawRecord || !navigator.clipboard?.writeText) return
    try {
      await navigator.clipboard.writeText(stringifyJson(rawRecord))
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1200)
    } catch (error) {
      flashMessage('error', 'Unable to copy the event record')
    }
  }

  const downloadRawRecord = () => {
    if (!rawRecord) return
    const blob = new Blob([stringifyJson(rawRecord)], { type: 'application/json' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `event-${rawRecord.id || 'record'}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  const pageStart = filteredRows.length ? ((currentPage - 1) * pageSize) + 1 : 0
  const pageEnd = Math.min(currentPage * pageSize, filteredRows.length)
  const stackTrace = rawRecord && selectedRow && isErrorEvent(selectedRow)
    ? findStackTrace(rawRecord)
    : null
  const diagnostics = rawRecord ? storedEventData(rawRecord).diagnostics : null

  return (
    <section className="admin-metrics-panel">
      <div className="event-stream-heading">
        <div>
          <span className="admin-section-kicker">Event stream</span>
          <h2>Latest log events</h2>
          <p>Scan normalized activity, then open any event for its complete persisted record.</p>
        </div>
        <div className="admin-metrics-toolbar-left">
          <button type="button" onClick={handleLoad} disabled={isLoading} className="admin-metrics-button">
            <BusyContent busy={isLoading} label={"Refreshing…"}>{'Refresh events'}</BusyContent>
          </button>
          <label className="admin-metrics-limit-control" htmlFor="admin-metrics-limit">
            <span>Load</span>
            <input id="admin-metrics-limit" type="number" min="1" max={MAX_LIMIT} value={limit} onChange={event => setLimit(event.target.value)} />
          </label>
        </div>
      </div>

      <div className="admin-metrics-kpis">
        <article><span>Loaded</span><strong>{rows.length}</strong><small>Newest retained events</small></article>
        <article className={failures ? 'attention' : ''}><span>Failures</span><strong>{failures}</strong><small>Require attention</small></article>
        <article><span>Tool calls</span><strong>{toolCalls}</strong><small>All outcomes</small></article>
        <article><span>Users</span><strong>{uniqueUsers}</strong><small>In loaded events</small></article>
      </div>

      <div className="event-quick-filters" aria-label="Quick event filters">
        {QUICK_FILTERS.map(filter => (
          <button key={filter.value} type="button" className={quickFilter === filter.value ? 'active' : ''} onClick={() => { setQuickFilter(filter.value); setCurrentPage(1) }}>
            {filter.label}
          </button>
        ))}
      </div>

      <div className="admin-metrics-filters">
        <label className="admin-metrics-field admin-metrics-field-search"><span>Search</span><input placeholder="Event, trace, context, or user" value={globalSearch} onChange={event => { setGlobalSearch(event.target.value); setCurrentPage(1) }} /></label>
        <label className="admin-metrics-field"><span>User</span><select value={selectedEmail} onChange={event => { setSelectedEmail(event.target.value); setCurrentPage(1) }}><option value="all">All users</option>{emailOptions.map(email => <option key={email} value={email}>{email}</option>)}</select></label>
        <label className="admin-metrics-field"><span>Event</span><select value={selectedAction} onChange={event => { setSelectedAction(event.target.value); setCurrentPage(1) }}><option value="all">All events</option>{actionOptions.map(action => <option key={action} value={action}>{action}</option>)}</select></label>
        <label className="admin-metrics-field"><span>From (PT)</span><input type="date" value={startDate} onChange={event => { setStartDate(event.target.value); setCurrentPage(1) }} /></label>
        <label className="admin-metrics-field"><span>To (PT)</span><input type="date" value={endDate} onChange={event => { setEndDate(event.target.value); setCurrentPage(1) }} /></label>
        <button type="button" onClick={clearFilters} className="admin-metrics-button tertiary">Clear filters</button>
      </div>

      <div className="admin-metrics-subheader">
        <span>Showing {filteredRows.length} of {rows.length} loaded events</span>
        <span>{lastLoadedAt ? `Updated ${formatTimestampPst(lastLoadedAt)} PT` : 'Not loaded yet'}</span>
      </div>

      <div className="event-stream-table-wrap" aria-busy={isLoading}>
        <table className="event-stream-table">
          <thead><tr><th>Time</th><th>Event</th><th>Context</th><th>Service</th><th>Status</th><th>Duration</th><th>User</th><th><span className="sr-only">Details</span></th></tr></thead>
          <tbody>
            {pagedRows.map(row => (
              <tr key={row.id} onClick={() => openDetails(row)} onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') openDetails(row) }} tabIndex="0">
                <td><time title={`${formatTimestampPst(row.timestamp)} PT`}>{formatRelativeTime(row.timestamp)}</time><small>{formatTimestampPst(row.timestamp)}</small></td>
                <td><strong>{eventDisplayName(row)}</strong><small>{titleCase(row.eventType)}</small></td>
                <td><div className="event-context-chips">{contextEntries(row.context).length ? contextEntries(row.context).map(([key, value]) => <span key={key}>{contextLabel(row, key)}: {String(value)}</span>) : <span className="empty">No additional context</span>}</div></td>
                <td><span className="event-service">{titleCase(row.service)}</span></td>
                <td><span className={`event-status ${statusClass(row.status)}`}>{titleCase(row.status)}</span></td>
                <td>{formatDuration(row.durationMs)}</td>
                <td><span className="event-user" title={row.email || row.userId}>{row.email || row.userId || 'System'}</span></td>
                <td><button type="button" className="event-view-button" onClick={event => { event.stopPropagation(); openDetails(row) }} aria-label={`View ${eventDisplayName(row)} event details`}>View</button></td>
              </tr>
            ))}
            {!pagedRows.length && <tr><td colSpan="8" className="event-stream-empty">{isLoading ? <LoadingState label="Loading latest events…" /> : 'No events match these filters.'}</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="admin-metrics-pagination">
        <span>{filteredRows.length ? `${pageStart}–${pageEnd} of ${filteredRows.length}` : 'No events'}</span>
        <div className="admin-metrics-pagination-controls">
          <label htmlFor="admin-metrics-page-size">Rows</label>
          <select id="admin-metrics-page-size" value={pageSize} onChange={event => { setPageSize(Number(event.target.value)); setCurrentPage(1) }}><option value="10">10</option><option value="25">25</option><option value="50">50</option></select>
          <button type="button" onClick={() => setCurrentPage(previous => Math.max(1, previous - 1))} disabled={currentPage === 1}>Previous</button>
          <span>Page {currentPage} of {totalPages}</span>
          <button type="button" onClick={() => setCurrentPage(previous => Math.min(totalPages, previous + 1))} disabled={currentPage === totalPages}>Next</button>
        </div>
      </div>

      {selectedRow ? (
        <div className="event-detail-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) closeDetails() }}>
          <aside className="event-detail-drawer" role="dialog" aria-modal="true" aria-labelledby="event-detail-title">
            <header><div><span className="admin-section-kicker">Complete stored event</span><h2 id="event-detail-title">{eventDisplayName(selectedRow)}</h2><p>{formatTimestampPst(selectedRow.timestamp)} PT · Event #{selectedRow.id}</p></div><button type="button" onClick={closeDetails} className="event-detail-close" aria-label="Close event details">×</button></header>
            <div className="event-detail-summary">
              <div><span>Service</span><strong>{titleCase(selectedRow.service)}</strong></div>
              <div><span>Status</span><strong className={`event-status ${statusClass(selectedRow.status)}`}>{titleCase(selectedRow.status)}</strong></div>
              <div><span>Duration</span><strong>{formatDuration(selectedRow.durationMs)}</strong></div>
              <div><span>User</span><strong>{selectedRow.email || selectedRow.userId || 'System'}</strong></div>
            </div>
            {contextEntries(selectedRow.context).length ? <section className="event-detail-section"><h3>Event context</h3><div className="event-detail-context">{contextEntries(selectedRow.context).map(([key, value]) => <div key={key}><span>{contextLabel(selectedRow, key)}</span><strong>{String(value)}</strong></div>)}</div></section> : null}
            <section className="event-detail-section event-correlation"><h3>Correlation</h3><dl><div><dt>Trace</dt><dd>{selectedRow.traceId || '—'}</dd></div><div><dt>Run</dt><dd>{selectedRow.runId || '—'}</dd></div><div><dt>Session</dt><dd>{selectedRow.sessionId || '—'}</dd></div></dl></section>
            {diagnostics ? <section className="event-detail-section error-diagnostics-section"><h3>Failed API exchange</h3><h4>Request</h4><pre data-testid="error-request-details">{stringifyJson(diagnostics.request || {})}</pre><h4>Response</h4><pre data-testid="error-response-details">{stringifyJson(diagnostics.response || {})}</pre></section> : null}
            {rawRecord && isErrorEvent(selectedRow) ? <section className="event-detail-section error-stack-section"><h3>Full stack trace</h3>{stackTrace ? <pre data-testid="error-stack-trace">{stackTrace}</pre> : <div className="event-detail-message">No stack trace was stored for this event.</div>}</section> : null}
            <section className="event-detail-section raw-event-section">
              <div className="raw-event-heading"><div><h3>Full persisted record</h3><p>No display filtering or truncation.</p></div><div><button type="button" onClick={copyRawRecord} disabled={!rawRecord}>{copied ? 'Copied' : 'Copy JSON'}</button><button type="button" onClick={downloadRawRecord} disabled={!rawRecord}>Download</button></div></div>
              {detailLoading ? <LoadingState label="Loading complete record…" /> : detailError ? <div className="event-detail-message error" role="alert">{detailError}</div> : <pre>{rawRecord ? stringifyJson(rawRecord) : 'No record loaded.'}</pre>}
            </section>
          </aside>
        </div>
      ) : null}
    </section>
  )
}

export default UserActionTable
